import Link from "next/link";
import type { PortableTextComponentProps } from "@portabletext/react";
import {
  OakHeading,
  OakFlex,
  OakBox,
  OakTertiaryButton,
} from "@oaknational/oak-components";

import type { VideoLocationValueType } from "@/browser-lib/avo/Avo";
import { PortableTextJSON, TextAndMedia } from "@/common-lib/cms-types";
import { OmitKeepDiscriminated } from "@/utils/generics";
import { getLinkHref } from "@/utils/portableText/resolveInternalHref";
import CMSImage from "@/components/SharedComponents/CMSImage";
import VideoPlayer from "@/components/SharedComponents/VideoPlayer";
import { PortableTextWithDefaults } from "@/components/SharedComponents/PortableText";

// When we get the JSON portable text it doesn't have the same field names as
// our generic types / what comes from our graphql queries
type TextAndMediaBlock = OmitKeepDiscriminated<
  TextAndMedia,
  "bodyPortableText"
> & {
  body: PortableTextJSON;
};

export type PostTextAndMediaProps =
  PortableTextComponentProps<TextAndMediaBlock> & {
    location?: VideoLocationValueType;
  };

const PostTextAndMedia = (props: PostTextAndMediaProps) => {
  if (!props.value) {
    return null;
  }

  const params = props.value;

  // @TODO: Responsive handling - likely don't want it reversed
  const flexDirection = params.alignMedia === "left" ? "row-reverse" : "row";

  return (
    <OakFlex
      $flexDirection={flexDirection}
      $alignItems={"center"}
      $mt="spacing-56"
    >
      <div>
        <OakHeading $font={["heading-5", "heading-4"]} tag="h2">
          {params.title}
        </OakHeading>
        <OakBox $mt="spacing-32">
          <PortableTextWithDefaults value={params.body} />
        </OakBox>
        {params.cta && (
          <OakTertiaryButton
            $mt={"spacing-24"}
            element={Link}
            href={getLinkHref(params.cta)}
          >
            {params.cta.label}
          </OakTertiaryButton>
        )}
      </div>
      {params.mediaType === "image" && params.image && (
        <OakBox $mr="spacing-24">
          <CMSImage image={params.image} />
        </OakBox>
      )}
      {params.mediaType === "video" && params.video && (
        <OakBox $mr="spacing-24">
          <VideoPlayer
            title={params.video.title}
            playbackId={params.video.video.asset.playbackId}
            location={props.location ?? "blog"}
            playbackPolicy="public"
          />
        </OakBox>
      )}
    </OakFlex>
  );
};

export default PostTextAndMedia;
